import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ArcadeChallengeEvent } from '@/types';
import {
  type ArcadeChallengeFormData,
  emptyChallengeForm,
  Field,
  toPayload,
} from './ArcadeChallengeForm';

interface CreateArcadeChallengeDialogProps {
  events: ArcadeChallengeEvent[];
}

export function CreateArcadeChallengeDialog({
  events,
}: CreateArcadeChallengeDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm<ArcadeChallengeFormData>(
    emptyChallengeForm(events[0]?.id ?? ''),
  );
  const { data, setData, processing, reset, transform } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform(toPayload);
    form.post('/admin/arcade-challenges', {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={events.length === 0}>
        <Plus className="mr-2 h-4 w-4" />
        Create Challenge
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Arcade Challenge</DialogTitle>
            <DialogDescription>
              Create a challenge window for a specific event.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Event" error={errors.eventId}>
              <Select
                value={data.eventId}
                onValueChange={(value) => setData('eventId', value)}
                disabled={processing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} ({event.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Name" error={errors.name}>
              <Input
                placeholder="Arcade Challenge 2026"
                disabled={processing}
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
              />
            </Field>

            <Field label="Slug" error={errors.slug}>
              <Input
                placeholder="arcade-2026"
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="Submission Deadline"
                error={errors.submissionDeadline}
              >
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.submissionDeadline}
                  onChange={(e) =>
                    setData('submissionDeadline', e.target.value)
                  }
                />
              </Field>
              <Field label="Voting Deadline" error={errors.votingDeadline}>
                <Input
                  type="datetime-local"
                  disabled={processing}
                  value={data.votingDeadline}
                  onChange={(e) => setData('votingDeadline', e.target.value)}
                />
              </Field>
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={processing || events.length === 0}
              >
                {processing ? 'Creating...' : 'Create Challenge'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
